import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import {
  FaHandHoldingHeart, FaTimes, FaCheck, FaInfoCircle,
  FaRupeeSign, FaFileInvoice, FaHeart, FaTags, FaCalendarAlt
} from "react-icons/fa";
import Navbar from "./Navbar";
import Footer from "./Footer";

/* ─────────────────────────────────────────────────────────────
   DONATION TYPE DEFINITIONS (static — no API needed)
───────────────────────────────────────────────────────────── */
const DONATION_TYPES = [
  {
    id: "Monthly Contribution",
    label: "Monthly Contribution",
    labelMl: "മാസ വിഹിതം",
    icon: <FaCalendarAlt />,
    description: "Support the temple's monthly rituals and maintenance through your regular contribution.",
  },
  {
    id: "Annual Pooja Contribution",
    label: "Annual Pooja Contribution",
    labelMl: "വാർഷിക പൂജ വിഹിതം",
    icon: <FaHandHoldingHeart />,
    description: "Be a part of the grand annual pooja celebrations and earn divine blessings.",
  },
  {
    id: "Other",
    label: "Other Contribution",
    labelMl: "മറ്റ് സംഭാവന",
    icon: <FaHeart />,
    description: "Offer your heartfelt contribution for any specific purpose or occasion.",
  },
];

/* ═══════════════════════════════════════════════════
   TOAST
═══════════════════════════════════════════════════ */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const cfg = {
    success: { icon: <FaCheck />,      label: "Blessings",     accent: "#2a6b2a", iconBg: "rgba(42,107,42,0.12)",   border: "rgba(42,107,42,0.3)",   symbol: "✦" },
    error:   { icon: <FaTimes />,      label: "Error",         accent: "#8b1a1a", iconBg: "rgba(139,26,26,0.10)",  border: "rgba(139,26,26,0.28)",  symbol: "✗" },
    info:    { icon: <FaInfoCircle />, label: "Divine Notice", accent: "#8a6020", iconBg: "rgba(201,151,58,0.10)", border: "rgba(201,151,58,0.30)", symbol: "ॐ" },
  };
  const c = cfg[type] || cfg.info;

  return (
    <div className="dn-toast-wrap">
      <div className="dn-toast" style={{ "--accent": c.accent, "--border": c.border }}>
        <div className="dn-toast-bar" />
        <span className="dn-tc tl" /><span className="dn-tc tr" />
        <span className="dn-tc bl" /><span className="dn-tc br" />
        <div className="dn-toast-medal" style={{ background: c.iconBg, borderColor: c.border }}>
          <div className="dn-toast-spin" />
          <span style={{ color: c.accent, fontSize: 14, position: "relative", zIndex: 1 }}>{c.icon}</span>
        </div>
        <div className="dn-toast-body">
          <div className="dn-toast-row">
            <span style={{ color: c.accent, fontSize: 9 }}>{c.symbol}</span>
            <span className="dn-toast-title" style={{ color: c.accent }}>{c.label}</span>
            <span style={{ color: c.accent, fontSize: 9 }}>{c.symbol}</span>
          </div>
          <div className="dn-toast-rule" style={{ background: `linear-gradient(90deg,transparent,${c.accent}55,transparent)` }} />
          <p className="dn-toast-msg">{message}</p>
        </div>
        <button className="dn-toast-x" onClick={onClose} style={{ color: c.accent }}>✕</button>
        <div className="dn-toast-foot" />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   DONATION FORM MODAL
═══════════════════════════════════════════════════ */
function DonationFormModal({ donationType, user, onClose, onConfirm, processing }) {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name:    user?.name || "",
    address: "",
    amount:  "",
    remarks: "",
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
  };

  const validate = () => {
    const errs = {};
    if (!formData.name.trim())    errs.name    = "Please enter your name";
    if (!formData.address.trim()) errs.address = "Please enter your address";
    if (!formData.amount || isNaN(formData.amount) || parseFloat(formData.amount) <= 0)
      errs.amount = "Please enter a valid amount";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onConfirm({
      name:          formData.name.trim(),
      address:       formData.address.trim(),
      donation_type: donationType.id,
      amount:        parseFloat(formData.amount),
      remarks:       formData.remarks.trim() || null,
    });
  };

  if (!donationType) return null;

  return createPortal(
    <div className="dn-modal-overlay" onClick={onClose}>
      <div className="dn-regal-modal dn-edit-regal" onClick={e => e.stopPropagation()}>
        <div className="dn-rm-topglow" />
        <span className="dn-cm-corner tl" /><span className="dn-cm-corner tr" />
        <span className="dn-cm-corner bl" /><span className="dn-cm-corner br" />

        {/* Header */}
        <div className="dn-rm-header">
          <div className="dn-rm-header-bg" />
          <div className="dn-rm-header-inner">
            <div className="dn-rm-header-icon-wrap">
              <div className="dn-rm-hicon-ring" />
              <span className="dn-rm-hicon">{donationType.icon}</span>
            </div>
            <div className="dn-rm-header-eyebrow">✦ {t("templeName") || "Temple"} ✦</div>
            <h2 className="dn-rm-title">{t("donModAdd") || "Make a Donation"}</h2>
            <p className="dn-rm-name-tag">{donationType.label}</p>
          </div>
          <button className="dn-rm-close" onClick={onClose} disabled={processing}><FaTimes /></button>
        </div>
        <div className="dn-rm-rule"><span className="dn-rm-rule-ornament">❖</span></div>

        {/* Body */}
        <div className="dn-edit-body">

          {/* Donation Type — readonly display */}
          <div className="dn-edit-section-label">✦ {t("donSecDetails") || "Donation Details"}</div>
          <div className="dn-edit-grid" style={{ marginBottom: 20 }}>
            <div className="dn-edit-field full-width">
              <label className="dn-edit-label">{t("donLblType") || "Donation Type"}</label>
              <div className="dn-edit-wrap dn-edit-wrap-readonly">
                <span className="dn-edit-prefix"><FaTags /></span>
                <span className="dn-readonly-val">{donationType.label}</span>
              </div>
            </div>
          </div>

          {/* Donor Details */}
          <div className="dn-edit-section-label">✦ {t("donSecDonor") || "Donor Details"}</div>
          <div className="dn-edit-grid">

            {/* Name */}
            <div className="dn-edit-field full-width">
              <label className="dn-edit-label">{t("modEditLblName") || "Full Name"} <span className="dn-req">*</span></label>
              <div className={`dn-edit-wrap ${errors.name ? "err" : ""}`}>
                <input
                  type="text" name="name" className="dn-edit-input"
                  placeholder={t("modEditPhName") || "Your full name"}
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>
              {errors.name && <span className="dn-edit-err">{errors.name}</span>}
            </div>

            {/* Address */}
            <div className="dn-edit-field full-width">
              <label className="dn-edit-label">{t("lblAddress") || "Address"} <span className="dn-req">*</span></label>
              <div className={`dn-edit-wrap ${errors.address ? "err" : ""}`}>
                <input
                  type="text" name="address" className="dn-edit-input"
                  placeholder={t("modEditPhAddress") || "Your address"}
                  value={formData.address}
                  onChange={handleChange}
                />
              </div>
              {errors.address && <span className="dn-edit-err">{errors.address}</span>}
            </div>

            {/* Amount */}
            <div className="dn-edit-field full-width">
              <label className="dn-edit-label">{t("catLblAmount") || "Amount"} (₹) <span className="dn-req">*</span></label>
              <div className={`dn-edit-wrap ${errors.amount ? "err" : ""}`}>
                <span className="dn-edit-prefix"><FaRupeeSign /></span>
                <input
                  type="number" name="amount" className="dn-edit-input"
                  placeholder="0.00" min="1"
                  value={formData.amount}
                  onChange={handleChange}
                />
              </div>
              {errors.amount && <span className="dn-edit-err">{errors.amount}</span>}
            </div>

            {/* Remarks */}
            <div className="dn-edit-field full-width">
              <label className="dn-edit-label">{t("donLblRemarks") || "Remarks"}</label>
              <div className="dn-edit-wrap dn-edit-wrap-textarea">
                <textarea
                  name="remarks" className="dn-edit-textarea"
                  placeholder={t("donPhRemarks") || "Any note or occasion (optional)"}
                  rows={2}
                  value={formData.remarks}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="dn-edit-actions">
            <button className="dn-btn dn-btn-ghost" onClick={onClose} disabled={processing}>
              {t("btnCancel") || "Cancel"}
            </button>
            <button className="dn-edit-save-btn" onClick={handleSubmit} disabled={processing}>
              {processing
                ? <><span className="dn-edit-saving-spinner" />{t("bkgRegistering") || "Processing"}…</>
                : <><FaRupeeSign /> {formData.amount && parseFloat(formData.amount) > 0
                    ? `Pay ₹${parseFloat(formData.amount).toLocaleString("en-IN")}`
                    : (t("donBtnCreate") || "Donate Now")
                  }</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════ */
export default function Donations() {
  const API       = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const navigate  = useNavigate();
  const { t }     = useTranslation();

  const [user, setUser]                 = useState(null);
  const [processing, setProcessing]     = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [toast, setToast]               = useState(null);
  const [logoFace, setLogoFace]         = useState("om");

  const razorpayScriptRef = useRef(null);
  const showToast = (message, type = "info") => setToast({ message, type });

  // Logo flip
  useEffect(() => {
    const timer = setInterval(() => setLogoFace(f => f === "om" ? "logo" : "om"), 3000);
    return () => clearInterval(timer);
  }, []);

  // Load Razorpay
  useEffect(() => {
    if (!window.Razorpay) {
      const script = document.createElement("script");
      script.src   = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => { razorpayScriptRef.current = true; };
      document.body.appendChild(script);
    } else {
      razorpayScriptRef.current = true;
    }
  }, []);

  // Auth check
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token      = localStorage.getItem("authToken");
    if (!storedUser || !token) { navigate("/login"); return; }
    try { setUser(JSON.parse(storedUser)); } catch { navigate("/login"); }
  }, [navigate]);

  /* ── STEP A: Create Razorpay Order ── */
  const handleDonationConfirm = async ({ name, address, donation_type, amount, remarks }) => {
    if (!user?.mobile) return;
    setProcessing(true);
    try {
      const orderRes = await fetch(`${API}/api/create-donation-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({
          amount:   Math.round(amount * 100),
          currency: "INR",
          receipt:  `don_${Date.now()}`,
        }),
      });
      if (!orderRes.ok) throw new Error("Order creation failed");
      const orderData = await orderRes.json();

      // ── STEP B: Open Razorpay ──
      const options = {
        key:         process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount:      orderData.amount,
        currency:    orderData.currency,
        name:        "Kodungallur Kavil Sree Bhadrakali Devi Temple",
        description: donation_type,
        order_id:    orderData.id,
        handler: async (response) => {
          await verifyAndSave(response, orderData, { name, address, donation_type, amount, remarks });
        },
        prefill: { name: user.name, email: user.email || "", contact: user.mobile },
        theme:   { color: "#c9973a" },
        modal:   { ondismiss: () => { setProcessing(false); showToast("Payment cancelled", "info"); } },
      };

      new window.Razorpay(options).open();

    } catch (err) {
      console.error(err);
      showToast(err.message, "error");
      setProcessing(false);
    }
  };

  /* ── STEP C: Verify & Save to DB ── */
  const verifyAndSave = async (rzpResp, orderData, donationData) => {
    try {
      const res = await fetch(`${API}/api/verify-donation-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({
          razorpay_order_id:   orderData.id,
          razorpay_payment_id: rzpResp.razorpay_payment_id,
          razorpay_signature:  rzpResp.razorpay_signature,
          name:          donationData.name,
          address:       donationData.address,
          donation_type: donationData.donation_type,
          amount:        donationData.amount,
          remarks:       donationData.remarks,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        showToast("Donation successful! Thank you for your contribution 🙏", "success");
        setSelectedType(null);
        setTimeout(() => navigate("/Mybooking"), 2500);
      } else {
        throw new Error(data.error || "Payment verification failed");
      }
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <DnStyles />
      <Navbar />

      {/* ══ HERO ══ */}
      <div className="dn-hero">
        <div className="dn-hero-bg" />
        <div className="dn-hero-rings">
          {[220, 170, 125, 85].map((s, i) => (
            <div key={i} className="dn-hero-ring" style={{ width: s, height: s, animationDelay: `${i * 0.5}s` }} />
          ))}
        </div>

        {/* Left side column */}
        <div className="dn-hero-side-col dn-side-left">
          <div className="dn-hlc-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="dn-hlc-tick" style={{ opacity: 0.15 + i * 0.1, height: 3 + i * 2 }} />
            ))}
          </div>
          <div className="dn-hlc-om">ॐ</div>
          <div className="dn-hlc-line" />
          <span className="dn-hlc-symbol">◈</span>
          <div className="dn-hlc-line" />
          <div className="dn-hlc-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="dn-hlc-tick" style={{ opacity: 0.85 - i * 0.1, height: 3 + (6 - i) * 2 }} />
            ))}
          </div>
        </div>

        {/* Center */}
        <div className="dn-hero-center">
          <div className="dn-hero-medallion">
            <div className="dn-hero-med-ring" />
            <div className="dn-hero-med-ring dn-hero-med-ring-2" />
            <div className={`dn-hero-face ${logoFace === "om" ? "visible" : ""}`}>
              <span className="dn-hero-om">ॐ</span>
            </div>
            <div className={`dn-hero-face ${logoFace === "logo" ? "visible" : ""}`}>
              <img src="./logo.png" alt="Temple" className="dn-hero-logo-img" />
            </div>
            <div className="dn-hero-med-halo" />
          </div>

          <div className="dn-hero-text">
            <div className="dn-hero-eyebrow">✦ {t("templeName") || "Devotee Portal"} ✦</div>
            <h1 className="dn-hero-title">{t("donPageTitle") || "Donations"}</h1>
            <p className="dn-hero-sub">{t("donPageSub") || "Select a donation type to make your offering"}</p>
            <button className="dn-mybookings-btn" onClick={() => navigate("/Mybooking")}>
              <FaFileInvoice /> {t("myDonationsBtn") || "My Donations"}
            </button>
          </div>

          <div className="dn-hero-rule" />
        </div>

        {/* Right side column */}
        <div className="dn-hero-side-col dn-side-right">
          <div className="dn-hlc-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="dn-hlc-tick" style={{ opacity: 0.15 + i * 0.1, height: 3 + i * 2 }} />
            ))}
          </div>
          <div className="dn-hlc-om">ॐ</div>
          <div className="dn-hlc-line" />
          <span className="dn-hlc-symbol">◈</span>
          <div className="dn-hlc-line" />
          <div className="dn-hlc-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="dn-hlc-tick" style={{ opacity: 0.85 - i * 0.1, height: 3 + (6 - i) * 2 }} />
            ))}
          </div>
        </div>

      </div>

      {/* ══ DONATION TYPE CARDS — same layout as bookings.js category grid ══ */}
      <div className="dn-offerings-wrap">
        <div className="dn-don-grid">
          {DONATION_TYPES.map((dtype, i) => (
            <div
              key={dtype.id}
              className="dn-don-card"
              style={{ animationDelay: `${i * 0.1}s` }}
              onClick={() => setSelectedType(dtype)}
            >
              {/* Medallion */}
              <div className="dn-don-medallion">
                <div className="dn-don-med-ring" />
                <div className="dn-hero-face visible">
                  <span className="dn-don-icon">{dtype.icon}</span>
                </div>
              </div>

              <div className="dn-don-name">{dtype.label}</div>
              <p className="dn-don-name-ml">{dtype.labelMl}</p>

              {dtype.description && (
                <p className="dn-don-desc">{dtype.description}</p>
              )}

              <div className="dn-don-footer">
                <button
                  className="dn-don-btn"
                  onClick={e => { e.stopPropagation(); setSelectedType(dtype); }}
                >
                  <FaHandHoldingHeart /> {t("donBtnCreate") || "Donate Now"}
                </button>
              </div>

              <span className="dn-don-corner vz-tl" />
              <span className="dn-don-corner vz-br" />
            </div>
          ))}
        </div>
      </div>

      {/* ══ MODAL ══ */}
      {selectedType && (
        <DonationFormModal
          donationType={selectedType}
          user={user}
          onClose={() => { if (!processing) setSelectedType(null); }}
          onConfirm={handleDonationConfirm}
          processing={processing}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <Footer />
    </>
  );
}

/* ═══════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════ */
function DnStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

      :root {
        --gold-bright: #e8c46a; --gold-mid: #c9973a; --gold-dim: #8a6020;
        --crimson-deep: #1a0404; --crimson-mid: #2c0808;
        --hero-1: #2e0c0c; --hero-2: #1c0505; --hero-3: #100202;
        --parchment: #FDF3E3; --text-dark: #1a0404; --text-mid: #4a1a08;
      }
      *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

      /* ══ HERO ══ */
      .dn-hero { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1) 0%, var(--hero-2) 50%, var(--hero-3) 100%); display: grid; grid-template-columns: 72px 1fr 72px; align-items: stretch; }
      .dn-hero-bg { position: absolute; inset: 0; pointer-events: none; background: radial-gradient(ellipse at 15% 30%, rgba(160,35,35,0.35) 0%, transparent 50%), radial-gradient(ellipse at 85% 70%, rgba(80,15,15,0.3) 0%, transparent 45%); }
      .dn-hero-rings { position: absolute; top: 30px; right: 60px; width: 230px; height: 230px; pointer-events: none; }
      .dn-hero-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); border-radius: 50%; border: 1px solid rgba(232,196,106,0.07); animation: dnRingPulse 4s ease-in-out infinite alternate; }
      .dn-hero-ring:nth-child(odd) { border-style: dashed; }
      @keyframes dnRingPulse { from { opacity: 0.3; } to { opacity: 0.8; } }

      /* Side columns */
      .dn-hero-side-col { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 28px 0; position: relative; z-index: 2; }
      .dn-side-left { border-right: 1px solid rgba(201,151,58,0.12); }
      .dn-side-right { border-left: 1px solid rgba(201,151,58,0.12); }
      .dn-hlc-om { font-family: "Cinzel Decorative", cursive; font-size: 20px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 0 8px rgba(232,196,106,0.5)); line-height: 1; }
      .dn-hlc-line { width: 1px; flex: 1; min-height: 16px; background: linear-gradient(180deg, transparent, rgba(201,151,58,0.3), transparent); }
      .dn-hlc-symbol { font-size: 16px; color: rgba(232,196,106,0.5); line-height: 1; }
      .dn-hlc-tick { width: 2px; border-radius: 1px; background: linear-gradient(180deg, var(--gold-bright), var(--gold-mid)); }
      .dn-hlc-ornament { display: flex; flex-direction: column; align-items: center; gap: 3px; }

      /* Center column */
      .dn-hero-center { display: flex; flex-direction: column; align-items: center; padding: 40px 32px 0; position: relative; z-index: 2; }

      .dn-hero-medallion { width: 90px; height: 90px; border-radius: 50%; background: radial-gradient(circle at 40% 35%, #5a1515 0%, #2a0707 55%, #0e0101 100%); border: 1px solid rgba(232,196,106,0.3); display: flex; align-items: center; justify-content: center; position: relative; margin-bottom: 20px; box-shadow: 0 0 30px rgba(232,196,106,0.15), inset 0 1px 0 rgba(255,255,255,0.06); }
      .dn-hero-med-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.25); animation: dnSlowSpin 10s linear infinite; }
      .dn-hero-med-ring-2 { inset: -10px; border-style: dotted; border-color: rgba(232,196,106,0.12); animation-duration: 18s; animation-direction: reverse; }
      @keyframes dnSlowSpin { to { transform: rotate(360deg); } }
      .dn-hero-face { position: absolute; inset: 0; border-radius: 50%; display: flex; align-items: center; justify-content: center; opacity: 0; transform: scale(0.7) rotateY(90deg); transition: opacity 0.55s ease, transform 0.55s ease; }
      .dn-hero-face.visible { opacity: 1; transform: scale(1) rotateY(0deg); }
      .dn-hero-om { font-family: "Cinzel Decorative", cursive; font-size: 34px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 0 8px rgba(232,196,106,0.5)); }
      .dn-hero-logo-img { width: 54px; height: 54px; object-fit: contain; filter: drop-shadow(0 0 8px rgba(232,196,106,0.4)); }
      .dn-hero-med-halo { position: absolute; inset: -16px; border-radius: 50%; background: radial-gradient(circle, rgba(232,196,106,0.08) 0%, transparent 65%); pointer-events: none; }
      .dn-hero-text { text-align: center; position: relative; z-index: 1; }
      .dn-hero-eyebrow { font-family: "Cinzel", serif; font-size: 8.5px; letter-spacing: 2px; color: rgba(232,196,106,0.35); text-transform: uppercase; margin-bottom: 10px; }
      .dn-hero-title { font-family: "Cinzel Decorative", cursive; font-size: 28px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.2; margin-bottom: 8px; }
      .dn-hero-sub { font-family: "EB Garamond", serif; font-style: italic; font-size: 15px; color: rgba(245,228,176,0.4); margin-bottom: 20px; }
      .dn-mybookings-btn { display: inline-flex; align-items: center; gap: 7px; margin-bottom: 28px; font-family: "Cinzel", serif; font-size: 9.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--crimson-deep); background: linear-gradient(135deg, #7a4e0c 0%, var(--gold-mid) 20%, var(--gold-bright) 45%, #fff8e7 55%, var(--gold-bright) 70%, var(--gold-mid) 85%, #6b4209 100%); border: none; padding: 11px 24px; cursor: pointer; clip-path: polygon(12px 0%, calc(100% - 12px) 0%, 100% 50%, calc(100% - 12px) 100%, 12px 100%, 0% 50%); box-shadow: 0 4px 18px rgba(201,151,58,0.28); transition: all 0.3s ease; }
      .dn-mybookings-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(201,151,58,0.48); filter: brightness(1.07); }
      .dn-hero-rule { width: 60%; max-width: 400px; height: 1px; margin: 0 auto; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); }

      /* ══ CARDS GRID ══ */
      .dn-offerings-wrap { padding: 36px 28px 60px; background: #f8ead4; }
      .dn-don-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; max-width: 1100px; margin: 0 auto; }
      .dn-don-card { position: relative; padding: 32px 22px 24px; border: 1px solid rgba(232,196,106,0.1); background: #0e0101; overflow: hidden; clip-path: polygon(0 0, calc(100% - 18px) 0, 100% 18px, 100% 100%, 18px 100%, 0 calc(100% - 18px)); cursor: pointer; transition: all 0.35s ease; animation: dnCardIn 0.4s ease both; display: flex; flex-direction: column; align-items: center; gap: 10px; text-align: center; }
      @keyframes dnCardIn { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      .dn-don-card::before { content: ""; position: absolute; top: 0; left: 0; width: 3px; height: 0; background: linear-gradient(180deg, #e8c46a, #c9973a); transition: height 0.35s ease; }
      .dn-don-card:hover::before { height: 100%; }
      .dn-don-card:hover { border-color: rgba(232,196,106,0.22); transform: translateY(-3px); box-shadow: 0 14px 36px rgba(0,0,0,0.4); }

      /* Medallion */
      .dn-don-medallion { width: 64px; height: 64px; border-radius: 50%; background: radial-gradient(circle at 40% 35%, #5a1515 0%, #2a0707 55%, #0e0101 100%); border: 1px solid rgba(232,196,106,0.35); display: flex; align-items: center; justify-content: center; position: relative; box-shadow: 0 0 20px rgba(232,196,106,0.1); flex-shrink: 0; margin-bottom: 4px; }
      .dn-don-med-ring { position: absolute; inset: -4px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.25); animation: dnSlowSpin 12s linear infinite; }
      .dn-don-icon { font-size: 22px; background: linear-gradient(180deg, #fff8e7, #e8c46a, #c9973a); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 0 6px rgba(232,196,106,0.4)); position: relative; z-index: 1; }

      /* Card Text */
      .dn-don-name { font-family: "Cinzel", serif; font-size: 15px; font-weight: bold; letter-spacing: 1px; text-transform: uppercase; background: linear-gradient(135deg, #fffaf0, #e8c46a); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .dn-don-name-ml { font-family: "Noto Serif Malayalam", "EB Garamond", serif; font-size: 13px; color: rgba(232,196,106,0.45); margin-top: -4px; }
      .dn-don-desc { font-family: "EB Garamond", serif; font-size: 14px; color: rgba(245,228,176,0.5); line-height: 1.6; flex: 1; }

      /* Card Footer */
      .dn-don-footer { width: 100%; display: flex; justify-content: center; margin-top: 8px; padding-top: 14px; border-top: 1px solid rgba(232,196,106,0.1); }
      .dn-don-btn { display: inline-flex; align-items: center; gap: 6px; font-family: "Cinzel", serif; font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: #1a0404; background: linear-gradient(135deg, #c9973a, #e8c46a, #c9973a); border: none; padding: 8px 20px; cursor: pointer; clip-path: polygon(8px 0%, calc(100% - 8px) 0%, 100% 50%, calc(100% - 8px) 100%, 8px 100%, 0% 50%); transition: all 0.3s ease; }
      .dn-don-btn:hover { filter: brightness(1.1); transform: scale(1.05); }
      .dn-don-corner { position: absolute; width: 12px; height: 12px; border-color: rgba(201,151,58,0.35); border-style: solid; }
      .dn-don-corner.vz-tl { top: 8px; left: 8px; border-width: 1.5px 0 0 1.5px; }
      .dn-don-corner.vz-br { bottom: 8px; right: 8px; border-width: 0 1.5px 1.5px 0; }

      /* ══ MODAL ══ */
      .dn-modal-overlay { position: fixed; inset: 0; z-index: 1000; background: rgba(8,1,1,0.85); backdrop-filter: blur(7px); display: flex; align-items: center; justify-content: center; padding: 20px; animation: dnOverlayIn 0.2s ease; }
      @keyframes dnOverlayIn { from { opacity: 0; } to { opacity: 1; } }
      .dn-regal-modal { position: relative; width: 100%; background: linear-gradient(160deg, #FDF3E3 0%, #faf0dc 55%, #f5e8cc 100%); border: 1px solid rgba(201,151,58,0.35); box-shadow: 0 36px 110px rgba(26,4,4,0.7), 0 0 0 1px rgba(201,151,58,0.06) inset; animation: dnRegalIn 0.35s cubic-bezier(0.23,1,0.32,1) both; overflow: hidden; }
      @keyframes dnRegalIn { from { opacity: 0; transform: scale(0.93) translateY(26px); } to { opacity: 1; transform: scale(1) translateY(0); } }
      .dn-edit-regal { max-width: 560px; max-height: 92vh; overflow-y: auto; }
      .dn-edit-regal::-webkit-scrollbar { width: 6px; }
      .dn-edit-regal::-webkit-scrollbar-thumb { background: linear-gradient(180deg, var(--hero-1), #5a1515, var(--gold-mid)); border-radius: 10px; }
      .dn-rm-topglow { position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, transparent 0%, rgba(201,151,58,0.2) 20%, rgba(232,196,106,0.9) 50%, rgba(201,151,58,0.2) 80%, transparent 100%); z-index: 10; }
      .dn-cm-corner { position: absolute; width: 16px; height: 16px; border-color: rgba(201,151,58,0.5); border-style: solid; z-index: 10; }
      .dn-cm-corner.tl { top: 14px; left: 14px; border-width: 1.5px 0 0 1.5px; }
      .dn-cm-corner.tr { top: 14px; right: 14px; border-width: 1.5px 1.5px 0 0; }
      .dn-cm-corner.bl { bottom: 14px; left: 14px; border-width: 0 0 1.5px 1.5px; }
      .dn-cm-corner.br { bottom: 14px; right: 14px; border-width: 0 1.5px 1.5px 0; }
      .dn-rm-header { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1), var(--hero-2) 55%, var(--hero-3)); padding: 38px 28px 28px; text-align: center; border-bottom: 1px solid rgba(201,151,58,0.2); }
      .dn-rm-header-bg { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 40%, rgba(150,30,30,0.28) 0%, transparent 65%); pointer-events: none; }
      .dn-rm-header-inner { position: relative; z-index: 1; display: flex; flex-direction: column; align-items: center; gap: 8px; }
      .dn-rm-close { position: absolute; top: 16px; right: 16px; z-index: 5; background: rgba(255,255,255,0.06); border: 1px solid rgba(232,196,106,0.2); color: rgba(232,196,106,0.55); border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; transition: all 0.2s; }
      .dn-rm-close:hover { background: rgba(255,255,255,0.12); color: var(--gold-bright); transform: rotate(90deg); }
      .dn-rm-close:disabled { opacity: 0.3; pointer-events: none; }
      .dn-rm-header-icon-wrap { position: relative; width: 72px; height: 72px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: rgba(232,196,106,0.08); border: 1px solid rgba(232,196,106,0.2); }
      .dn-rm-hicon-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.28); animation: dnSlowSpin 9s linear infinite; }
      .dn-rm-hicon { font-size: 28px; color: var(--gold-bright); filter: drop-shadow(0 0 10px rgba(232,196,106,0.4)); position: relative; z-index: 1; }
      .dn-rm-header-eyebrow { font-family: "Cinzel", serif; font-size: 7.5px; letter-spacing: 3px; color: rgba(232,196,106,0.3); text-transform: uppercase; }
      .dn-rm-title { font-family: "Cinzel Decorative", cursive; font-size: 22px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.25; }
      .dn-rm-name-tag { font-family: "Cinzel", serif; font-size: 11px; letter-spacing: 1px; color: rgba(245,228,176,0.4); }
      .dn-rm-rule { display: flex; align-items: center; justify-content: center; height: 1px; margin: 0 40px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); position: relative; z-index: 1; }
      .dn-rm-rule-ornament { position: absolute; font-size: 12px; color: var(--gold-mid); background: var(--parchment); padding: 0 8px; line-height: 1; }

      /* Form */
      .dn-edit-body { padding: 22px 28px 28px; position: relative; z-index: 1; }
      .dn-edit-section-label { font-family: "Cinzel", serif; font-size: 8px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: var(--gold-dim); margin-bottom: 18px; padding-bottom: 7px; border-bottom: 1px solid rgba(201,151,58,0.15); }
      .dn-edit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
      .dn-edit-grid .full-width { grid-column: span 2; }
      .dn-edit-field { display: flex; flex-direction: column; gap: 5px; }
      .dn-edit-label { font-family: "Cinzel", serif; font-size: 8.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); }
      .dn-req { color: #b52020; font-weight: 900; margin-left: 2px; }
      .dn-edit-wrap { display: flex; align-items: center; background: #fff9f0; border: 1px solid rgba(201,151,58,0.28); clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); overflow: hidden; transition: border-color 0.3s, box-shadow 0.3s; }
      .dn-edit-wrap:focus-within { border-color: var(--gold-mid); background: #fffaf0; box-shadow: 0 0 0 3px rgba(201,151,58,0.12); }
      .dn-edit-wrap.err { border-color: #b52020; background: rgba(181,32,32,0.02); }
      .dn-edit-wrap-readonly { background: #f0ece4; cursor: default; }
      .dn-edit-wrap-readonly:focus-within { box-shadow: none; border-color: rgba(201,151,58,0.28); }
      .dn-edit-wrap-textarea { clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 10px, 100% calc(100% - 10px), calc(100% - 10px) 100%, 10px 100%, 0% calc(100% - 10px), 0% 10px); align-items: flex-start; }
      .dn-edit-prefix { font-family: "Cinzel", serif; font-size: 11px; font-weight: 600; color: var(--text-mid); padding: 13px 12px 13px 20px; border-right: 1px solid rgba(201,151,58,0.2); flex-shrink: 0; }
      .dn-edit-input { flex: 1; background: transparent; border: none; outline: none; font-family: "EB Garamond", serif; font-size: 15px; color: var(--text-dark); padding: 13px 14px; }
      .dn-edit-input::placeholder { color: rgba(74,26,8,0.25); font-style: italic; }
      .dn-readonly-val { flex: 1; font-family: "Cinzel", serif; font-size: 13px; font-weight: 700; color: var(--text-dark); padding: 13px 14px; }
      .dn-edit-textarea { flex: 1; background: transparent; border: none; outline: none; font-family: "EB Garamond", serif; font-size: 15px; color: var(--text-dark); padding: 13px 14px; resize: vertical; min-height: 70px; line-height: 1.5; }
      .dn-edit-textarea::placeholder { color: rgba(74,26,8,0.25); font-style: italic; }
      .dn-edit-err { font-size: 10px; color: #b52020; font-family: "EB Garamond", serif; font-style: italic; margin-top: 2px; }
      .dn-edit-actions { display: flex; gap: 10px; align-items: center; justify-content: center; margin-top: 4px; padding-top: 18px; border-top: 1px solid rgba(201,151,58,0.12); }
      .dn-btn { display: inline-flex; align-items: center; gap: 7px; font-family: "Cinzel", serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; border: none; padding: 10px 18px; cursor: pointer; clip-path: polygon(10px 0%,calc(100% - 10px) 0%,100% 50%,calc(100% - 10px) 100%,10px 100%,0 50%); transition: all 0.2s; border: 1px solid transparent; }
      .dn-btn:disabled { opacity: 0.38; cursor: not-allowed; }
      .dn-btn-ghost { background: none; border-color: rgba(201,151,58,0.28); color: rgba(74,26,8,0.5); }
      .dn-btn-ghost:hover { border-color: rgba(201,151,58,0.5); background: rgba(201,151,58,0.06); }
      .dn-edit-save-btn { display: inline-flex; align-items: center; gap: 8px; font-family: "Cinzel", serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--crimson-deep); background: linear-gradient(135deg, #7a4e0c 0%, var(--gold-mid) 20%, var(--gold-bright) 40%, #fff8e7 55%, var(--gold-bright) 70%, var(--gold-mid) 85%, #6b4209 100%); border: none; padding: 13px 28px; cursor: pointer; clip-path: polygon(14px 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0% 50%); box-shadow: 0 6px 24px rgba(201,151,58,0.3); transition: all 0.35s ease; }
      .dn-edit-save-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(201,151,58,0.5); filter: brightness(1.05); }
      .dn-edit-save-btn:disabled { opacity: 0.38; cursor: not-allowed; transform: none; box-shadow: none; }
      .dn-edit-saving-spinner { width: 12px; height: 12px; border-radius: 50%; border: 2px solid rgba(26,4,4,0.2); border-top-color: var(--crimson-deep); animation: dnSpin 0.7s linear infinite; flex-shrink: 0; }
      @keyframes dnSpin { to { transform: rotate(360deg); } }

      /* Toast */
      .dn-toast-wrap { position: fixed; top: 28px; right: 28px; z-index: 4000; animation: dnToastIn 0.38s cubic-bezier(0.23,1,0.32,1) both; }
      @keyframes dnToastIn { from { opacity: 0; transform: translateX(60px); } to { opacity: 1; transform: none; } }
      .dn-toast { width: 340px; background: linear-gradient(145deg, #FDF3E3, #faf0dc, #f5e8cc); border: 1px solid var(--border); border-radius: 2px; box-shadow: 0 12px 32px rgba(26,4,4,0.14); padding: 0 0 14px; overflow: hidden; position: relative; }
      .dn-toast-bar { height: 3px; background: linear-gradient(90deg, transparent, var(--accent) 20%, var(--accent) 80%, transparent); margin-bottom: 16px; }
      .dn-tc { position: absolute; width: 9px; height: 9px; border-color: rgba(201,151,58,0.4); border-style: solid; }
      .dn-tc.tl { top: 11px; left: 11px; border-width: 1px 0 0 1px; }
      .dn-tc.tr { top: 11px; right: 11px; border-width: 1px 1px 0 0; }
      .dn-tc.bl { bottom: 7px; left: 11px; border-width: 0 0 1px 1px; }
      .dn-tc.br { bottom: 7px; right: 11px; border-width: 0 1px 1px 0; }
      .dn-toast-medal { position: absolute; left: 18px; top: 26px; width: 34px; height: 34px; border-radius: 50%; border: 1px solid; display: flex; align-items: center; justify-content: center; }
      .dn-toast-spin { position: absolute; inset: -3px; border-radius: 50%; border: 1px dashed rgba(201,151,58,0.28); animation: dnSlowSpin 8s linear infinite; }
      .dn-toast-body { padding: 0 40px 0 64px; }
      .dn-toast-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
      .dn-toast-title { font-family: "Cinzel", serif; font-size: 9.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; flex: 1; }
      .dn-toast-rule { height: 1px; margin-bottom: 6px; }
      .dn-toast-msg { font-family: "EB Garamond", serif; font-style: italic; font-size: 14px; color: rgba(74,26,8,0.72); line-height: 1.5; }
      .dn-toast-foot { height: 1px; margin: 12px 18px 0; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.22), transparent); }
      .dn-toast-x { position: absolute; top: 20px; right: 13px; background: none; border: none; font-size: 12px; cursor: pointer; opacity: 0.4; transition: opacity 0.2s; }
      .dn-toast-x:hover { opacity: 0.85; }

      /* Responsive */
      @media (max-width: 900px) { .dn-don-grid { grid-template-columns: repeat(2, 1fr); } }
      @media (max-width: 640px) {
        .dn-hero { grid-template-columns: 32px 1fr 32px; }
        .dn-hero-title { font-size: 20px; }
        .dn-hlc-om { font-size: 14px; }
        .dn-hlc-symbol { font-size: 11px; }
        .dn-mybookings-btn { clip-path: none; border-radius: 28px; }
        .dn-offerings-wrap { padding: 20px 14px 40px; }
        .dn-don-grid { grid-template-columns: 1fr; gap: 14px; }
        .dn-don-card { clip-path: none; border-radius: 14px; }
        .dn-don-btn { clip-path: none; border-radius: 20px; }
        .dn-modal-overlay { padding: 0; align-items: flex-end; }
        .dn-regal-modal { clip-path: none !important; border-radius: 22px 22px 0 0 !important; max-height: 93vh; border-bottom: none; width: 100%; max-width: 100% !important; }
        .dn-edit-body { padding: 16px 16px 28px; }
        .dn-edit-grid { grid-template-columns: 1fr; gap: 12px; }
        .dn-edit-grid .full-width { grid-column: span 1; }
        .dn-edit-wrap { clip-path: none; border-radius: 28px; }
        .dn-edit-wrap-textarea { clip-path: none; border-radius: 14px; }
        .dn-edit-save-btn { clip-path: none; border-radius: 28px; flex: 1; justify-content: center; }
        .dn-edit-actions .dn-btn { clip-path: none; border-radius: 28px; }
        .dn-toast-wrap { top: auto; bottom: 14px; right: 14px; left: 14px; }
        .dn-toast { width: 100%; clip-path: none; border-radius: 12px; }
      }
    `}</style>
  );
}